// Atlas shell — full-bleed map is the home, ⌘K command palette in the
// center, left timeline rail, right dossier with the answer envelope.
// Reuses the existing MapPresentation / StateChoroplethPresentation under
// the hood — only the chrome differs.
import ReactMarkdown from 'react-markdown';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PresentationContainer } from '../components/presentation/PresentationContainer';
import { CommandPalette } from './atlas/CommandPalette';
import type { PaletteSection } from './atlas/CommandPalette';
import { DEMO_QUESTIONS } from '../demoQuestions';
import { safeUrl } from '../safeUrl';
import type { ShellProps } from './types';
import type { Presentation, Turn } from '../types';

type Layer = 'HPSA' | 'MUA' | 'NHSC' | 'Ryan White' | 'Health Centers' | 'Grants';
const LAYERS: Layer[] = ['HPSA', 'MUA', 'NHSC', 'Ryan White', 'Health Centers', 'Grants'];

const PINNED_KEY = 'hrsa.atlas.pinned';

function loadPinned(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(PINNED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function savePinned(set: Set<string>) {
  try {
    window.localStorage.setItem(PINNED_KEY, JSON.stringify([...set]));
  } catch {
    // Persistence is best-effort; the in-memory set still works for the session.
  }
}

// Standing US-states bubble fallback so the map area is never blank.
// Centroid + value=0 — just outlines the country shape with marker positions.
const FALLBACK_CHOROPLETH: Presentation = {
  type: 'state_choropleth',
  title: 'United States · pick a state',
  metric_label: 'standing layer',
  values: [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
    'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
    'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  ].map((state) => ({ state, value: 1, label: state })),
};

function presentationForAtlas(turn: ShellProps['current']): Presentation {
  if (!turn) return FALLBACK_CHOROPLETH;
  return turn.assistant.presentation ?? FALLBACK_CHOROPLETH;
}

function timelineLabelFor(query: string): string {
  return query.length > 56 ? query.slice(0, 53) + '…' : query;
}

function agoString(ms: number | undefined, now: number): string {
  if (!ms) return 'now';
  const sec = Math.max(1, Math.round((now - ms) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}

interface TimelineItem {
  id: string;
  query: string;
  primaryTool: string;
  ago: string;
  isCurrent: boolean;
}

function buildTimeline(props: ShellProps, now: number, currentToolKey: string | null): TimelineItem[] {
  const items: TimelineItem[] = [];
  if (props.current) {
    items.push({
      id: '__current',
      query: props.current.query,
      primaryTool: currentToolKey ?? (props.current.assistant.toolsUsed[0] ?? '…'),
      ago: props.current.streaming ? 'streaming' : agoString(props.current.assistant.doneAt, now),
      isCurrent: true,
    });
  }
  // priorTurns lacks tool info on the wire; render with a placeholder badge.
  for (let i = props.priorTurns.length - 1; i >= 0; i--) {
    const t = props.priorTurns[i];
    items.push({
      id: `prior-${i}`,
      query: t.user_query,
      primaryTool: 'PRIOR',
      ago: '—',
      isCurrent: false,
    });
  }
  return items;
}

// Pull palette items from the shared demo question set so this stays in
// lockstep with EmptyState + the Demo runner. Group by partialDataCoverage
// so users see at a glance which questions exercise the wired-up tools vs
// the ones with bulk-data gaps.
const PALETTE_SECTIONS: PaletteSection[] = [
  {
    label: 'Fully wired (ArcGIS / Proximity / Grants)',
    items: DEMO_QUESTIONS.filter((q) => !q.partialDataCoverage).map((q) => ({
      id: q.id.toLowerCase(),
      text: q.query,
      short: q.id,
      icon: '▦',
    })),
  },
  {
    label: 'Partial dataset coverage (UDS / MIECHV not yet wired)',
    items: DEMO_QUESTIONS.filter((q) => q.partialDataCoverage).map((q) => ({
      id: q.id.toLowerCase(),
      text: q.query,
      short: q.id,
      icon: '◐',
    })),
  },
];

export function AtlasShell(props: ShellProps) {
  const { current, submit } = props;
  const [layer, setLayer] = useState<Layer>('HPSA');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteSeed, setPaletteSeed] = useState('');
  const [pinned, setPinned] = useState<Set<string>>(() => loadPinned());
  const [now, setNow] = useState(Date.now());

  // Cmd/Ctrl-K toggles the palette globally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      } else if (e.key === 'Escape') {
        setPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Refresh "ago" timestamps every 30s so the timeline doesn't lie.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const togglePin = useCallback((id: string) => {
    setPinned((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      savePinned(next);
      return next;
    });
  }, []);

  const onSubmit = useCallback((q: string) => {
    setPaletteOpen(false);
    submit(q);
  }, [submit]);

  const liveTool = current?.toolsInFlight[0] ?? null;
  const timeline = useMemo(() => buildTimeline(props, now, liveTool), [props, now, liveTool]);

  const presentation = presentationForAtlas(current);
  const dossierOpen = Boolean(current);

  return (
    <div className="atlas-root">
      {/* Left timeline */}
      <aside className="atlas-timeline" aria-label="Question timeline">
        <div className="atlas-timeline__head">
          <span className="atlas-timeline__title">Timeline</span>
          <span className="atlas-timeline__count">{timeline.length}</span>
        </div>
        {timeline.length === 0 && (
          <div className="atlas-timeline__new" role="note">No questions yet — press ⌘K to ask.</div>
        )}
        {timeline.map((item) => (
          <button
            key={item.id}
            className={`atlas-timeline__item ${item.isCurrent ? 'atlas-timeline__item--active' : ''}`}
            onClick={() => { /* future: re-open prior turn in dossier */ }}
          >
            <span className="atlas-timeline__dot" aria-hidden="true" />
            <span className="atlas-timeline__body">
              <span className="atlas-timeline__row-top">
                <span className="atlas-timeline__label" title={item.query}>{timelineLabelFor(item.query)}</span>
                <span
                  className="atlas-timeline__pin"
                  role="button"
                  tabIndex={0}
                  aria-label={pinned.has(item.id) ? 'Unpin' : 'Pin'}
                  onClick={(e) => { e.stopPropagation(); togglePin(item.id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePin(item.id); } }}
                >
                  {pinned.has(item.id) ? '📌' : '☆'}
                </span>
              </span>
              <span className="atlas-timeline__meta">
                <span className="atlas-timeline__tool-pill">{item.primaryTool}</span>
                <span className="atlas-timeline__ago">{item.ago}</span>
              </span>
            </span>
          </button>
        ))}
        <div className="atlas-legend">
          <div className="atlas-legend__title">Active layer</div>
          <div className="atlas-layer-switcher" role="group" aria-label="Active map layer">
            {LAYERS.map((l) => (
              <button
                key={l}
                className={`atlas-layer-switcher__btn ${l === layer ? 'atlas-layer-switcher__btn--active' : ''}`}
                onClick={() => setLayer(l)}
                aria-pressed={l === layer}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Center map area */}
      <div className="atlas-mapwrap">
        <PresentationContainer p={presentation} />
        <button className="atlas-ask-trigger" onClick={() => { setPaletteSeed(''); setPaletteOpen(true); }}>
          <span className="atlas-ask-trigger__icon">⌘K</span>
          <span className="atlas-ask-trigger__text">Ask anything about HRSA programs…</span>
          <span aria-hidden="true">↵</span>
        </button>
      </div>

      {/* Right dossier */}
      {dossierOpen && current && <Dossier current={current} priorTurns={props.priorTurns} />}

      {/* ⌘K palette */}
      <CommandPalette
        open={paletteOpen}
        initialQuery={paletteSeed}
        sections={PALETTE_SECTIONS}
        onClose={() => setPaletteOpen(false)}
        onSubmit={onSubmit}
      />
    </div>
  );
}

interface DossierProps {
  current: NonNullable<ShellProps['current']>;
  priorTurns: Turn[];
}

function Dossier({ current }: DossierProps) {
  const { assistant, query, streaming, toolsInFlight } = current;
  const cta = (assistant.presentation as { cta?: { label: string; url: string } } | undefined)?.cta;
  return (
    <aside className="atlas-dossier" aria-label="Answer dossier">
      <div className="atlas-dossier__head">
        <div>
          <div className="atlas-dossier__kicker">{streaming ? 'Asking…' : 'Answer'}</div>
          <div className="atlas-dossier__title" title={query}>{timelineLabelFor(query)}</div>
        </div>
      </div>

      {streaming && toolsInFlight.length > 0 && (
        <div className="tool-use-indicator" aria-live="polite">
          <span className="spinner" aria-hidden="true">✦</span>
          <span>Looking up</span>
          {toolsInFlight.map((t) => (
            <span key={t} className="tool-pill" aria-hidden="true">{t}</span>
          ))}
        </div>
      )}

      {assistant.presentation && assistant.presentation.type !== 'prose' && (
        <div className="atlas-dossier__section">
          <div className="atlas-dossier__section-label">Result</div>
          <PresentationContainer p={assistant.presentation} />
        </div>
      )}

      {assistant.prose && (
        <div className="atlas-dossier__section">
          <div className="atlas-dossier__section-label">Summary</div>
          <div className={`prose ${streaming ? 'prose--streaming' : ''}`} aria-live="polite" aria-busy={streaming}>
            {streaming ? (
              <>
                {assistant.prose}
                <span className="prose-cursor" aria-hidden="true">▍</span>
              </>
            ) : (
              <ReactMarkdown>{assistant.prose}</ReactMarkdown>
            )}
          </div>
        </div>
      )}

      {assistant.citations.length > 0 && (
        <div className="atlas-dossier__section">
          <div className="atlas-dossier__section-label">Sources</div>
          <ol className="atlas-dossier__src-list">
            {assistant.citations.map((c) => (
              <li key={c.index}>
                <a href={safeUrl(c.url)} target="_blank" rel="noopener noreferrer">{c.label}</a>
              </li>
            ))}
          </ol>
        </div>
      )}

      {assistant.toolsUsed.length > 0 && (
        <div className="atlas-dossier__section">
          <div className="atlas-dossier__section-label">Tools used</div>
          <div className="atlas-dossier__tools-row">
            {assistant.toolsUsed.map((t) => (
              <span key={t} className="atlas-dossier__tool-pill">{t}</span>
            ))}
          </div>
        </div>
      )}

      {assistant.error && <div className="assistant-error">⚠ {assistant.error}</div>}

      {cta && (
        <div className="atlas-dossier__actions">
          <a href={safeUrl(cta.url)} target="_blank" rel="noopener noreferrer" className="atlas-dossier__primary-btn" style={{ textAlign: 'center', textDecoration: 'none' }}>
            {cta.label} ↗
          </a>
        </div>
      )}
    </aside>
  );
}
